import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import Card from '../../components/common/Card/Card';
import { useAuth } from '../../hooks/useAuth';
import { Search } from '../../types/search.types';
import { FeedbackResponse } from '../../types/feedback.types';
import { TOKEN_STORAGE_KEY } from '../../utils/auth'; // Import the constant
// TODO: Define a proper type for FormSubmissionResponse
// import { FormSubmissionResponse } from '../../types/formSubmission.types'; 

import SearchesTable from '../../components/admin/SearchesTable';
import FormSubmissionsTable from '../../components/admin/FormSubmissionsTable';
import FeedbackTable from '../../components/admin/FeedbackTable';
import TrialInvitePanel from '../../components/admin/TrialInvitePanel';
import Pagination from '../../components/common/Pagination/Pagination';

const ITEMS_PER_PAGE = 20; // Default items per page matching backend default

const Admin: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading, fetchCurrentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAttempted = useRef(false);
  const userFetchedRef = useRef(false);
  
  const [allSearches, setAllSearches] = useState<Search[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [formSubmissions, setFormSubmissions] = useState<any[]>([]);
  const [feedbackEntries, setFeedbackEntries] = useState<FeedbackResponse[]>([]);
  const [loadingAllSearches, setLoadingAllSearches] = useState(true);
  const [errorAllSearches, setErrorAllSearches] = useState<string | null>(null);
  const [loadingFormSubmissions, setLoadingFormSubmissions] = useState(true);
  const [errorFormSubmissions, setErrorFormSubmissions] = useState<string | null>(null);
  const [loadingFeedback, setLoadingFeedback] = useState(true);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && !userFetchedRef.current) {
      fetchCurrentUser();
      userFetchedRef.current = true;
    }
  }, [isAuthenticated, fetchCurrentUser]);

  // Redirect logic
  useEffect(() => {
    if (!authLoading && !redirectAttempted.current) {
      if (user && user.is_admin !== true) {
        console.log('Admin: User is not an admin, redirecting to dashboard');
        navigate('/dashboard');
      } else if (!isAuthenticated) {
        console.log('Admin: User is not authenticated, redirecting to login');
        navigate('/login');
      }
      redirectAttempted.current = true;
    }
  }, [user, navigate, isAuthenticated, authLoading]);
  
  // Fetch data for admin panel (only if authenticated and admin)
  const fetchAdminData = async (pageToLoad: number) => {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY); // Use the correct key
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const apiUrl = process.env.REACT_APP_API_URL || ''; // Get API base URL

      // Fetch all searches (paginated)
      const searchesResponse = await fetch(`${apiUrl}/api/admin/searches?page=${pageToLoad}&items_per_page=${ITEMS_PER_PAGE}`, { headers });
      if (searchesResponse.ok) {
        const searchesData = await searchesResponse.json();
        setAllSearches(searchesData.items || []);
        setTotalPages(searchesData.total_pages || 1);
        setLoadingAllSearches(false);
      } else {
        console.error('Failed to fetch searches:', searchesResponse.status);
        setErrorAllSearches('Failed to fetch searches');
      }

      // Fetch all form submissions
      const formSubmissionsResponse = await fetch(`${apiUrl}/api/admin/form-submissions`, { headers });
      if (formSubmissionsResponse.ok) {
        const formSubmissionsData = await formSubmissionsResponse.json();
        setFormSubmissions(formSubmissionsData);
        setLoadingFormSubmissions(false);
      } else {
        console.error('Failed to fetch form submissions:', formSubmissionsResponse.status);
        setErrorFormSubmissions('Failed to fetch form submissions');
      }

      // Fetch all feedback
      const feedbackResponse = await fetch(`${apiUrl}/api/admin/feedback`, { headers });
      if (feedbackResponse.ok) {
        const feedbackData = await feedbackResponse.json();
        setFeedbackEntries(feedbackData);
        setLoadingFeedback(false);
      } else {
        console.error('Failed to fetch feedback:', feedbackResponse.status);
        setErrorFeedback('Failed to fetch feedback');
      }


    } catch (error) {
      console.error('Error fetching admin data:', error);
      setErrorAllSearches('Error fetching admin data');
      setErrorFormSubmissions('Error fetching admin data');
      setErrorFeedback('Error fetching admin data');
    }
  };

  useEffect(() => {
    if (isAuthenticated && user && user.is_admin === true) {
      fetchAdminData(currentPage);
    }
  }, [isAuthenticated, user, currentPage]); // Re-fetch searches when page changes

  // Handle page change for pagination
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      setLoadingAllSearches(true);
    }
  };

  // Show nothing during initial load/redirect or if not authorized
  if (authLoading || (!isAuthenticated) || !user || user.is_admin !== true) {
    return (
      <PageContainer>
        <Card>
          <div className="flex justify-center items-center py-12">
            {authLoading ? (
              <span className="text-gray-600">Loading...</span>
            ) : !isAuthenticated ? (
              <span className="text-gray-600">Please log in to access this page</span>
            ) : (
            <span className="text-gray-600">You don't have permission to access the admin panel</span>
            )}
          </div>
        </Card>
      </PageContainer>
    );
  }
  
  const isAnalyticsPage = location.pathname === '/admin/analytics';

  // Admin user content
  return (
    <PageContainer title="Admin Panel">
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8" aria-label="Tabs">
          <Link
            to="/admin"
            className={`${
              !isAnalyticsPage
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Overview
          </Link>
          <Link
            to="/admin/analytics"
            className={`${
              isAnalyticsPage
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Analytics
          </Link>
        </nav>
      </div>

      <div className="space-y-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Trial Invites</h2>
          <TrialInvitePanel />
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">All Searches</h2>
          {loadingAllSearches ? (
            <p className="text-sm text-gray-600">Loading searches...</p>
          ) : errorAllSearches ? (
            <p className="text-sm text-red-600">Error: {errorAllSearches}</p>
          ) : (
            <>
              <SearchesTable searches={allSearches} />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Form Submissions</h2>
          {loadingFormSubmissions ? (
            <p className="text-sm text-gray-600">Loading form submissions...</p>
          ) : errorFormSubmissions ? (
            <p className="text-sm text-red-600">Error: {errorFormSubmissions}</p>
          ) : (
            <FormSubmissionsTable formSubmissions={formSubmissions} />
          )}
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">User Feedback</h2>
          {loadingFeedback ? (
            <p className="text-sm text-gray-600">Loading feedback...</p>
          ) : errorFeedback ? (
            <p className="text-sm text-red-600">Error: {errorFeedback}</p>
          ) : (
            <FeedbackTable feedbackEntries={feedbackEntries} />
          )}
        </div>

      </div>
    </PageContainer>
  );
};

export default Admin; 